import {
    signInWithCustomToken,
    signOut,
    onAuthStateChanged,
    User
} from "firebase/auth";
import { doc, setDoc, arrayUnion, Timestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { authB, auth, functions, messaging, fs } from "../firebase.ts";
import { getToken } from "firebase/messaging";
import {
    LANGUAGE,
    DATE,
    deviceName,
    deviceType,
    osName,
    osVersion,
    browserName,
    browserVersion,
    isPwa,
    getIP,
    getLocation,
    isProduction
} from "../components/global.ts";
import Strings from "../models/Strings.json";
import { SwMap } from "../components/sw-main/sw-map/sw-map.ts";
import { Readers } from "./Readers.ts";

const strings: any = Strings;

type Status = {
    text: string;
    type: "success" | "error";
    user?: User | null;
};

export class HeartBank {
    private readers = new Readers();
    //private ip = ""; // also in localstorage??
    //private location: any = {};
    private hasAlertedAdmin = false; // one time per refresh

    //opener: Window | null = window.opener;
    hero: User | null = null;
    user: User | null = null;

    private readonly admins = ["thonly@heartbank.ceo", "sely0928@gmail.com"];
    private readonly admins2 = [
        "thonly@heartbank.ceo", // for testing as admin as english; dark mode
        "thonly@siliconwat.com" // for testing as user as khmer; light mode
    ];
    private readonly admins3 = [
        "sely0928@gmail.com",
        "andrewly1962@gmail.com",
        "sengsovann@gmail.com",
        "thonly@heartbank.ceo", // for testing as admin as english; dark mode
        "thonly@siliconwat.com" // for testing as user as khmer; light mode
    ];

    constructor() {
        onAuthStateChanged(authB, async (hero) => (this.hero = hero));
        onAuthStateChanged(auth, async (user) => (this.user = user));
    }

    public async saveTokenToFirestore() {
        try {
            const permission = await Notification.requestPermission();
            if (permission !== "granted") {
                alert(strings["notifications1"][LANGUAGE]);
                return;
            }

            const registration = await navigator.serviceWorker.ready;
            //const registration = await navigator.serviceWorker.getRegistration("/");

            const currentToken = await getToken(messaging, {
                vapidKey:
                    "BEc2kjaZKLwN7uafN-r4eMyTDtkRx3q7bAx-LrYmB1AlNhBTRuuFCyoF0QFo1PyOOnwvGbhG-LtSKLtu8OiltnM",
                serviceWorkerRegistration: registration
            });

            if (currentToken) {
                const userRef = doc(fs, "readers", this.getDeviceID());

                await setDoc(
                    userRef,
                    {
                        fcmTokens: [currentToken], //arrayUnion(currentToken),
                        lastActive: Timestamp.now()
                    },
                    { merge: true }
                );

                console.log("Token saved to Firestore successfully");
            }
        } catch (error) {
            console.error("Error saving token:", error);
        }
    }

    isLoggedIn(): boolean {
        return !!(this.user && this.hero);
    }

    addAuthStateListener(callback: (hero: User | null) => void) {
        return onAuthStateChanged(authB, callback); // returns unsubscribe function;
    }

    addAuthStateListener2(callback: (user: User | null) => void) {
        return onAuthStateChanged(auth, callback); // returns unsubscribe function;
    }

    addAuthStateListener3(
        callback: (hero: User | null, user: User | null) => void
    ) {
        onAuthStateChanged(authB, (hero) => {
            if (hero && this.user) callback(hero, this.user);
            else callback(null, null);
        });
        onAuthStateChanged(auth, (user) => {
            if (this.hero && user) callback(this.hero, user);
            else callback(null, null);
        });
    }

    waitForAuth(): Promise<User | null> {
        return new Promise((resolve) => {
            const unsubscribe = onAuthStateChanged(
                authB,
                (hero: User | null) => {
                    unsubscribe();
                    resolve(hero);
                }
            );
        });
    }

    waitForAuth2(): Promise<User | null> {
        return new Promise((resolve) => {
            const unsubscribe = onAuthStateChanged(
                auth,
                (user: User | null) => {
                    unsubscribe();
                    resolve(user);
                }
            );
        });
    }

    //TODO: later how to fix when user token expires but hero is still on ??
    async getUser(): Promise<User | null> {
        const users = await this.getUsers();
        if (users.user) {
            if (users.hero) {
                return this.hero;
            } else {
                await this.signOut();
                return null;
            }
        } else {
            if (users.hero) {
                const status = await this.signInWithIdToken(
                    await users.hero.getIdToken()
                );
                if (status.type === "success") return this.hero;
                else {
                    await this.signOut();
                    return null;
                }
            } else {
                await this.signOut();
                return null;
            }
        }
    }

    getUsers(): Promise<{ hero: User | null; user: User | null }> {
        return new Promise((resolve) => {
            // Monitor auth state for Project 1
            const unsubscribe1 = onAuthStateChanged(authB, (hero) => {
                this.hero = hero;
                checkAndResolve();
            });

            // Monitor auth state for Project 2
            const unsubscribe2 = onAuthStateChanged(auth, (user) => {
                this.user = user;
                checkAndResolve();
            });

            // Check if both users are signed in
            const checkAndResolve = () => {
                if (this.hero !== null && this.user !== null) {
                    resolve({ hero: this.hero, user: this.hero });
                    unsubscribe1();
                    unsubscribe2();
                }
            };
        });
    }

    // TODO: later maybe get id token from user (if exists) then call cloud function to get custom token; this will bypass vault login
    async signIn(token: string): Promise<Status> {
        try {
            const heartbank = await signInWithCustomToken(authB, token);
            this.hero = heartbank.user;
            if (this.user) {
                return {
                    text: strings["feedback17"][LANGUAGE],
                    type: "success",
                    user: this.user
                };
            } else return this.signIn2();
        } catch (error: any) {
            return {
                text: `Error signing in with custom token: ${error.message}`,
                type: "error"
            };
        }
    }

    // TODO: maybe later: allow logout of user only and login to user via passkey?
    async signIn2(): Promise<Status> {
        try {
            const idToken = await this.hero!.getIdToken();
            return await this.signInWithIdToken(idToken);
        } catch (error: any) {
            return {
                text: `Error signing in with id token: ${error.message}`,
                type: "error"
            };
        }
    }

    async signInWithIdToken(idToken: string): Promise<Status> {
        try {
            const generateLoginToken = httpsCallable<
                { idToken: string },
                { customToken: string }
            >(functions, "generateLoginToken", {
                limitedUseAppCheckTokens: true
            });
            const result = await generateLoginToken({ idToken });
            const customToken = result.data.customToken;

            const heartbank = await signInWithCustomToken(auth, customToken);
            this.user = heartbank.user;

            return {
                text: strings["feedback17"][LANGUAGE],
                type: "success",
                user: this.user
            };
        } catch (error: any) {
            return {
                text: `Error signing in with id token: ${error.message}`,
                type: "error"
            };
        }
    }

    async signOut(): Promise<Status> {
        if (this.hero) {
            try {
                await signOut(authB);
                this.hero = null; // TODO: keep tracking user ??
                return { text: strings.feedback13[LANGUAGE], type: "success" };
            } catch (error: any) {
                return { text: error.message, type: "error" };
            }
        } else return { text: strings.feedback13[LANGUAGE], type: "success" };
    }

    async signOut2(): Promise<Status> {
        if (this.user) {
            try {
                await signOut(auth);
                this.user = null; // TODO: keep tracking user ??
                return { text: strings.feedback12[LANGUAGE], type: "success" };
            } catch (error: any) {
                return { text: error.message, type: "error" };
            }
        } else return { text: strings.feedback12[LANGUAGE], type: "success" };
    }

    async signOut3(): Promise<Status> {
        if (this.hero && this.user) {
            try {
                await signOut(authB);
                await signOut(auth);
                this.hero = null; // TODO: keep tracking user ??
                this.user = null;
                return { text: strings.feedback12[LANGUAGE], type: "success" };
            } catch (error: any) {
                return { text: error.message, type: "error" };
            }
        } else if (this.hero) return this.signOut();
        else if (this.user) return this.signOut2();
        else return { text: strings.feedback12[LANGUAGE], type: "success" };
    }

    isRunningAsPWA(): boolean {
        const isStandalone = window.matchMedia(
            "(display-mode: standalone)"
        ).matches;
        const isIOSStandalone = (navigator as any).standalone === true; // iOS-specific
        return isStandalone || isIOSStandalone;
    }

    isSuperAdmin(): boolean {
        return this.isAdmin(this.admins2);
    }

    isAdmin(admins: string[] = this.admins): boolean {
        if (!this.user) return false;
        if (isProduction) {
            return !!(this.hero?.email && admins.includes(this.hero.email));
        } else {
            return this.hero !== null;
        }
    }

    async alertAdmin(action: string, value: string | null = null) {
        if (!navigator.onLine) return;

        const device = `${deviceName} ${osName} ${osVersion} (${deviceType})`;
        const browser = `${browserName} ${browserVersion} (${
            isPwa ? "PWA" : "Browser"
        })`;

        const ip = await getIP();
        // if (!this.ip) {
        //     const res = await fetch("https://api.ipify.org?format=json"); // must be on client bc don't want server ip
        //     const { ip } = await res.json();
        //     this.ip = ip;
        // }
        const location = await getLocation(ip);
        // if (this.location.ip !== this.ip) {
        //     const geoRes = await fetch(`https://ipapi.co/${this.ip}/json/`);
        //     this.location = await geoRes.json();
        //     this.location.ip = this.ip;
        //     //console.log(this.location);
        // }
        const map = await this.getMap(location);

        const colorScheme = document.documentElement.classList.contains("dark")
            ? "Dark Mode"
            : "Light Mode";

        const data = {
            id: this.getDeviceID(),
            url: window.location.href,
            action: value ? action + value : action,
            map,
            location,
            agent: navigator.userAgent,
            device,
            browser,
            version: DATE.formatDate(),
            colorScheme
        };

        const alertAdmin = httpsCallable<{
            id: string;
            url: string;
            action: string;
            map: any;
            location: any;
            agent: string;
            device: string;
            browser: string;
            version: string;
            colorScheme: string;
        }>(functions, "alertAdmin", { limitedUseAppCheckTokens: true });

        await this.updateReader(device, browser, location, map);
        console.info(data); // weird bug: need to be here before alertadmin

        if (this.isAdmin(this.admins3)) {
            if (!this.hasAlertedAdmin) {
                this.hasAlertedAdmin = true; // isProduction;
                await alertAdmin(data);
            }
        } else await alertAdmin(data);
    }

    // Anroid 9 supports web crypto
    public getDeviceID(): string {
        let deviceID = localStorage.getItem("device");
        if (!deviceID) {
            deviceID = window.crypto.randomUUID();
            localStorage.setItem("device", deviceID);
        }
        return deviceID;
    }

    private async getMap(location: any) {
        const component = document.querySelector("sw-map") as SwMap;
        const newLocation = await component.getLocation();
        const storedLocation = localStorage.getItem("location");

        if (newLocation) return newLocation;
        else if (storedLocation) return JSON.parse(storedLocation);
        else return {};

        // return Object.assign(
        //     {},
        //     location,
        //     (await component.getLocation()) ||
        //         JSON.parse(localStorage.getItem("location") || "{}")
        // );
    }

    private async updateReader(
        device: string,
        browser: string,
        location: any,
        map: any
    ) {
        const defaultLocation = {
            lat: location.latitude,
            lng: location.longitude,
            formatted_address:
                location.city +
                (location.region === location.city
                    ? ""
                    : " " + location.region) +
                " " +
                location.country_name
        };

        // same as cloud function
        // const user =
        //     this.location.ip +
        //     " " +
        //     this.getDevice() +
        //     " " +
        //     this.location.city +
        //     (this.location.region === this.location.city
        //         ? ""
        //         : " " + this.location.region) +
        //     " " +
        //     this.location.country_name;
        const user = `${this.getDeviceID()} | ${device} | ${browser}`;
        localStorage.setItem("user", user);

        const reader = await this.readers.update({
            id: this.getDeviceID(),
            uid: this.user?.uid || "", // logged in = public; logged out = anonymous
            user,
            address: map.formatted_address || defaultLocation.formatted_address,
            latitude: map.lat || defaultLocation.lat,
            longitude: map.lng || defaultLocation.lng
        });

        if (reader.status === "error") console.error(reader);
    }
}
